/**
 * Custom dropdown para el selector de ordenación.
 * Se registra como Custom Element para auto-inicializarse
 * cada vez que facet-form reemplaza el innerHTML de #SortByContainer.
 *
 * Usa un único handler onclick para evitar duplicación de listeners
 * cuando el DOM se recicla (drawer open/close, facets re-render).
 */
if (!customElements.get('wim-sort-dropdown')) {
  class WimSortDropdown extends HTMLElement {
    constructor() {
      super();
      this._onDocClick = this._handleDocClick.bind(this);
    }

    connectedCallback() {
      this._initRefs();

      // onclick se sobreescribe, no se acumula como addEventListener
      this.onclick = (e) => {
        const trigger = e.target.closest('.wim-sort-by-dropdown__trigger');
        if (trigger) {
          e.preventDefault();
          e.stopPropagation();
          this.toggle();
          return;
        }

        const option = e.target.closest('[data-sort-value]');
        if (option) {
          e.preventDefault();
          e.stopPropagation();
          this.selectOption(option);
          return;
        }
      };

      document.addEventListener('click', this._onDocClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      this.onclick = null;
    }

    /* ── Internal ─────────────────────────────────────────────── */

    _initRefs() {
      this.trigger = this.querySelector('.wim-sort-by-dropdown__trigger');
      this.list = this.querySelector('.wim-sort-by-dropdown__list');
      this.selectedText = this.querySelector('.wim-sort-by-dropdown__selected');

      // El <select> nativo puede estar dentro, ser el hermano anterior o estar en el padre
      const innerSelect = this.querySelector('select');
      const prev = this.previousElementSibling;
      if (innerSelect) {
        this.nativeSelect = innerSelect;
      } else if (prev && prev.tagName === 'SELECT') {
        this.nativeSelect = prev;
      } else {
        this.nativeSelect = this.parentElement ? this.parentElement.querySelector('select') : null;
      }
    }

    toggle() {
      if (!this.trigger) this._initRefs();
      const isOpen = this.classList.toggle('is-open');
      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', String(isOpen));
      }

      if (isOpen && this.list) {
        setTimeout(() => {
          const rect = this.list.getBoundingClientRect();
          const bottomMargin = 60; // Margen para dar aire y salvar botones flotantes
          if (rect.bottom + bottomMargin > window.innerHeight) {
            const scrollAmount = (rect.bottom + bottomMargin) - window.innerHeight;
            window.scrollBy({
              top: scrollAmount,
              behavior: 'smooth'
            });
          }

          const activeOption = this.list.querySelector('.is-active');
          if (activeOption) {
            activeOption.scrollIntoView({ block: 'nearest' });
          }
        }, 50);
      }
    }

    _handleDocClick(e) {
      // Si el click es fuera del componente, cerrar
      if (e && this.contains(e.target)) return;
      this.classList.remove('is-open');
      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', 'false');
      }
    }

    close() {
      this.classList.remove('is-open');
      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', 'false');
      }
    }

    selectOption(btn) {
      if (!this.selectedText || !this.list || !this.nativeSelect) this._initRefs();

      const value = btn.getAttribute('data-sort-value');

      // Actualizar texto visible
      if (this.selectedText) {
        this.selectedText.textContent = btn.textContent.trim();
      }

      // Marcar opción activa
      if (this.list) {
        this.list.querySelectorAll('.wim-sort-by-dropdown__option, .wim-store-dropdown__option').forEach((opt) => {
          opt.classList.remove('is-active');
        });
      }
      btn.classList.add('is-active');

      // Sincronizar con el <select> nativo y disparar change
      if (this.nativeSelect) {
        this.nativeSelect.value = value;
        this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Clase para estado de placeholder vs valor seleccionado
      if (this.trigger) {
        if (value && value.trim() !== '') {
          this.trigger.classList.add('has-value');
        } else {
          this.trigger.classList.remove('has-value');
        }
      }

      this.close();
    }
  }

  customElements.define('wim-sort-dropdown', WimSortDropdown);
}
